import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';
import moment from 'moment';
import classNames from 'classnames';
import { ButtonV2 } from '@zola/zola-ui';
import get from 'lodash/get';
import { getSlugSuggestions, checkSlugAvailability } from '../../../actions/OnboardActions';
import { RenderField } from '../../common/form';
import Tooltip from '../../common/ui/Tooltip/Tooltip';
import * as Validations from '../../common/form/commonValidations';

export class EditSlugComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      cleanSlug: true
    };

    this.fetchSlugSuggestions = this.fetchSlugSuggestions.bind(this);
    this.handleLiveUpdateSlug = this.handleLiveUpdateSlug.bind(this);
  }

  componentDidMount() {
    this.fetchSlugSuggestions();
  }

  getTooltipDetails() {
    const { hasWedding, hasRegistry } = this.props;
    const text = hasWedding && hasRegistry
      ? 'The URLs for both your website and registry will change to this edited text.'
      : 'You can preview your URL below before you update.';
    const width = hasWedding && hasRegistry ? 185 : 175;
    const boldText = hasWedding && hasRegistry ? ' Preview your URLs below before you update.' : null;
    const className = hasWedding && hasRegistry ? 'tooltip-lg' : null;

    return {
      text,
      width,
      boldText,
      className
    };
  }

  fetchSlugSuggestions() {
    const { handleGetSlugSuggestions, userContext } = this.props;
    const userData = get(userContext, 'user_role_account_weddings', {}) && get(userContext, 'user_role_account_weddings[0]', {});
    const formattedDate = userData.wedding_date ? moment.utc(userData.wedding_date).toDate() : '';

    const slugSuggestionData = {
      primaryFirstName: userData.owner_first_name,
      partnerFirstName: userData.partner_first_name,
      primaryLastName: userData.owner_last_name,
      partnerLastName: userData.partner_last_name,
      eventDate: formattedDate
    };

    handleGetSlugSuggestions(slugSuggestionData).then(response => response.payload);
  }

  handleLiveUpdateSlug(slug) {
    const {
      liveUpdateSlug,
      originalSlug,
      updateSlugForm
    } = this.props;

    liveUpdateSlug(slug);
    updateSlugForm(slug);

    if (slug !== originalSlug) {
      this.setState({
        cleanSlug: false
      });
    } else {
      this.setState({
        cleanSlug: true
      });
    }
  }

  // showTooltip & toggleEditSlugTooltip is passed from `UrlContainer` because `UrlContainer` will hide the tooltip upon editing the slug
  render() {
    const {
      handleSubmit,
      slug,
      invalid,
      submitting,
      slugSuggestions,
      updated,
      toggleEditSlugTooltip,
      showTooltip
    } = this.props;

    const tooltip = this.getTooltipDetails();
    const { cleanSlug } = this.state;

    return (
      <div className="edit-slug__container">
        <h2>Edit Your Unique URL
          <span
            className="zolaicon zolaicon-tips"
            onClick={toggleEditSlugTooltip}
          >
            <Tooltip
              text={tooltip.text}
              boldText={tooltip.boldText}
              visible={showTooltip && !invalid}
              width={tooltip.width}
              className={tooltip.className}
            />
          </span>
        </h2>
        <form
          onSubmit={handleSubmit}
        >
          <div className="edit-slug__form-group">
            <div className="edit-slug__input">
              <Field
                name="slug"
                type="text"
                component={RenderField}
                maxChars={80}
                autoComplete="off"
                required
                validate={[Validations.REQUIRED, Validations.MAX_LENGTH_80, Validations.SLUG]}
                onChange={e => this.handleLiveUpdateSlug(e.target.value)}
              />
              <span
                className={classNames(
                  { 'zolaicon zolaicon-close': slug && !updated },
                  { 'zolaicon zolaicon-checkmark': slug && updated }
                )}
                onClick={() => !updated && this.handleLiveUpdateSlug('')}
              />
            </div>
            <ButtonV2
              type="submit"
              disabled={cleanSlug || invalid}
              className="edit-slug__submit-btn"
            >
              {
                submitting ? 'Updating' : 'Update'
              }
            </ButtonV2>
          </div>
          <div className="edit-slug__suggestions">
            Other suggestions:
            {
              slugSuggestions && slugSuggestions.length > 0
                && slugSuggestions.map((suggestion, index) => (
                  <span
                    key={`edit-slug__suggestion-${index}`}
                    className="edit-slug__suggestion"
                    onClick={() => this.handleLiveUpdateSlug(suggestion)}
                  >{suggestion}
                  </span>
                ))
            }
          </div>
        </form>
      </div>
    );
  }
}

EditSlugComponent.propTypes = {
  slug: PropTypes.string,
  originalSlug: PropTypes.string,
  userContext: PropTypes.shape({}),
  handleGetSlugSuggestions: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
  invalid: PropTypes.bool,
  updated: PropTypes.bool,
  hasWedding: PropTypes.bool,
  hasRegistry: PropTypes.bool,
  submitting: PropTypes.bool,
  updateSlugForm: PropTypes.func,
  liveUpdateSlug: PropTypes.func,
  slugSuggestions: PropTypes.arrayOf(PropTypes.string),
  toggleEditSlugTooltip: PropTypes.func,
  showTooltip: PropTypes.bool
};

EditSlugComponent.defaultProps = {
  userContext: {},
};

const mapStateToProps = state => ({
  slugSuggestions: state.Onboard.slugSuggestions,
  initialValues: {
    // this passes the state into redux-form
    slug: state.user.userContext.user_role_account_weddings && state.user.userContext.user_role_account_weddings[0].slug,
  },
  // this allows state to be used as props in the component
  slug: state.user.userContext.user_role_account_weddings && state.user.userContext.user_role_account_weddings[0].slug,
});

const mapDispatchToProps = dispatch => ({
  handleGetSlugSuggestions: slugSuggestionData => dispatch(getSlugSuggestions(slugSuggestionData)),
  updateSlugForm: (newSlug) => {
    dispatch(change('editSlugForm', 'slug', newSlug));
  }
});

const asyncValidate = (values, dispatch, props) => {
  const { slug } = values;
  const dirtySlug = slug !== props.originalSlug;

  // don't check for suggestions if user enters their OG slug;
  return new Promise((resolve, reject) => {
    if (!dirtySlug) {
      return resolve();
    }

    return dispatch(checkSlugAvailability(slug))
      .then((response) => {
        if (!response.payload) {
          const error = { slug: 'This URL is already in use. Try another one.' };
          reject(error);
        }
        resolve();
      });
  });
};

const EditSlugForm = reduxForm({
  form: 'editSlugForm',
  destroyOnUnmount: false,
  asyncValidate,
  asyncBlurFields: ['slug'],
  enableReinitialize: true
})(EditSlugComponent);

const EditSlug = connect(
  mapStateToProps,
  mapDispatchToProps
)(EditSlugForm);

export default EditSlug;



// WEBPACK FOOTER //
// ./src/components/settings/url/EditSlug.jsx